import { ReactNode, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, ShieldAlert, ClipboardList } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface RequireStaffProps {
  children: ReactNode;
}

const RequireStaff = ({ children }: RequireStaffProps) => {
  const { user, isLoading, isSuperAdmin, hasStaffRole, isCoach } = useAuth();
  const navigate = useNavigate();
  const [showNotice, setShowNotice] = useState(false);

  // Auto-redirect after showing notice
  useEffect(() => {
    if (showNotice) {
      const timer = setTimeout(() => {
        navigate(isCoach ? "/coach-portal" : "/", { replace: true });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showNotice, isCoach, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!isSuperAdmin && !hasStaffRole) {
    if (!showNotice) {
      setShowNotice(true);
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-lg w-full border-2 border-amber-500/50 shadow-xl">
          <CardContent className="p-8 text-center space-y-6">
            <div className="h-20 w-20 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
              <ShieldAlert className="h-10 w-10 text-amber-500" />
            </div>
            
            <div>
              <h2 className="text-2xl font-bold mb-2">Admin Access Only</h2>
              <p className="text-lg text-muted-foreground">
                This area is restricted to league administrators. 
                {isCoach && " As a coach, your portal has everything you need!"}
              </p>
            </div>

            {isCoach ? (
              <Button 
                size="lg" 
                className="w-full text-lg py-6"
                onClick={() => navigate("/coach-portal", { replace: true })}
              >
                <ClipboardList className="h-5 w-5 mr-2" />
                Go to Coach Portal
              </Button>
            ) : (
              <Button 
                size="lg" 
                className="w-full text-lg py-6"
                onClick={() => navigate("/", { replace: true })}
              >
                Go to Homepage
              </Button>
            )}

            <p className="text-sm text-muted-foreground">
              Redirecting automatically in a few seconds...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default RequireStaff;
